<template lang="pug">
v-slide-x-transition
  v-row(v-if="loadingTask" key="loading")
    v-col(class="text-center")
      sdo-loader
  v-row(v-if="!loadingTask" key="content")
    v-col
      validation-observer(
        ref="obs"
        v-slot="{ invalid, handleSubmit }"
      )
        v-card.sdo-card
          v-card-text
            h1.mb-5 Добавить задание
            v-form(ref="form")
              v-radio-group(
                v-model="task.type"
                label="Тип задания"
                row
                mandatory
              )
                v-radio(
                  label="Автотест"
                  color="secondary"
                  :value="$conf.TASK_TYPES.auto"
                )
                v-radio(
                  label="Смешанное"
                  color="secondary"
                  :value="$conf.TASK_TYPES.mixed"
                )
              validation-provider(
                rules="required|max:2000"
                name="Название задания"
                vid="title"
                v-slot="{ errors, valid }"
              )
                v-textarea(
                  v-model="task.title"
                  auto-grow
                  rows="2"
                  row-height="20"
                  name="title"
                  label="Название задания"
                  :error-messages="errors"
                  @change="updateDraft"
                  filled
                  rounded
                  :counter="20000"
                )
              rich-text-editor.pb-4(
                v-model="task.description"
                rules="max:20000"
                name="description"
                label="Описание задания"
                vid="description"
                filled
                rounded
                :counter="20000"
              )
              validation-provider(
                name="Цель задания"
                vid="purpose"
                v-slot="{ errors, valid }"
              )
                v-select.mt-8(
                  v-model="task.purpose"
                  :items="taskResources.purpose"
                  item-text="title"
                  item-value="value"
                  name="purpose"
                  label="Цель задания"
                  @change="updateDraft"
                  :error-messages="errors"
                  filled
                  rounded
                )
              validation-provider(
                v-if="task.type === $conf.TASK_TYPES.mixed"
                name="Отметка"
                vid="mark"
                v-slot="{ errors, valid }"
              )
                v-select(
                  v-model="task.mark"
                  :items="taskResources.mark"
                  item-text="title"
                  item-value="value"
                  name="mark"
                  @change="updateDraft"
                  label="Отметка"
                  :error-messages="errors"
                  filled
                  rounded
                )
              validation-provider(
                rules="required"
                name="Количество попыток"
                vid="attempt"
                v-slot="{ errors, valid }"
              )
                v-text-field(
                  v-model="task.attempt"
                  name="attempt"
                  label="Количество попыток"
                  type="integer"
                  :error-messages="errors"
                  filled
                  rounded
                  @change="updateDraft"
                )
              template(v-if="task.type === $conf.TASK_TYPES.auto")
                validation-provider(
                  rules="required"
                  name="Минимальное кол-во баллов для прохождения"
                  vid="min_passing_score"
                  v-slot="{ errors, valid }"
                )
                  v-text-field(
                    v-model="task.min_passing_score"
                    name="min_passing_score"
                    label="Минимальное кол-во баллов для прохождения"
                    type="integer"
                    :error-messages="errors"
                    filled
                    rounded
                    @change="updateDraft"
                  )

        v-row.mt-3
          v-col
            task-unit-create(
              v-for="(taskUnit, index) in task.task_units"
              :key="index"
              v-show="!(taskUnit.method === 'delete')"
              :value="taskUnit"
              :index="index"
              :autotest="task.type === $conf.TASK_TYPES.auto"
              :ref="`taskUnit${index}`"
              @remove="removeTaskUnit(index, taskUnit)"
              @update="addUpdateMethod(taskUnit)"
              @change="updateDraft"
            )
        v-alert(
          v-if="answersCost < task.min_passing_score && hasOneAnswer"
          outlined
          type="info"
        ) Обратите внимание,
          |  у вас не добавлено нужное количество баллов для успешного выполнения задания.
        .mb-3
        sdo-back-button
        v-btn.mr-2(
          @click="update ? updateTask() : handleSubmit(createTask)"
          color="success"
          :loading="loadingButton"
          rounded
          depressed
          :disabled="invalid || notExistsPossibleAnswers"
        ) Сохранить

        tooltip-wrap(
          text=`Добавить вопрос к заданию нельзя, есть
            обучающиеся уже сдавшие результаты выполнения задания`
          :value="!canCreateQuestion"
        )
          v-btn(
            color="primary"
            rounded
            depressed
            :disabled="!canCreateQuestion"
            @click="addTaskUnit"
          )
            v-icon(left) mdi-plus
            | Добавить вопрос

</template>

<script>
import { mapGetters } from 'vuex';
import RichTextEditor from '@/components/InputFields/RichTextEditor.vue';

import TaskService from '@/services/API/TaskService';
import TaskUnitCreate from '@/modules/Tasks/TaskUnits/components/creation/TaskUnitCreate.vue';
import DirectionSelect from '@/components/InputFields/course/SelectDirection.vue';
import { invertColor } from '@/utils/color-functions';

import { TASK_UNIT_TYPE_NAMES } from '@/modules/Tasks/TaskUnits/constants';

export default {
  name: 'TaskCreate',
  components: { DirectionSelect, TaskUnitCreate, RichTextEditor },
  data: () => ({
    task: {
      title: '',
      description: '',
      direction: null,
      purpose: null,
      mark: null,
      tags: [],
    },
    canCreateQuestion: true,
    taskDraftId: null,
    tags: [],
    loadingButton: false,
    loadingTask: false,
  }),
  computed: {
    ...mapGetters(['taskResources']),
    ...mapGetters({ userData: 'auth/userData' }),
    notExistsPossibleAnswers() {
      if (!this.task?.task_units || !this.task?.task_units?.length) return true;
      return this.task.task_units.find((taskUnit) => {
        if ([
          TASK_UNIT_TYPE_NAMES.choiceSeveralOptions,
          TASK_UNIT_TYPE_NAMES.choiceOneOption,
        ].includes(taskUnit.type)
        ) {
          const hasntCorrectAnswer = !taskUnit.possible_answers
            .find((i) => i.correct_answer === true);
          const hasntCost = !taskUnit.possible_answers.some((i) => i.cost > 0);
          const isAoutoTest = this.task.type === this.$conf.TASK_TYPES.auto;
          return hasntCorrectAnswer || (isAoutoTest && hasntCost);
        }
        return false;
      });
    },
    answersCost() {
      let array = [];
      if (this.task?.task_units?.length) {
        array = array.concat(...this.task.task_units.map((i) => {
          const result = i?.possible_answers?.length ? i.possible_answers.map((it) => it.cost) : 0;
          return result;
        }));
      }
      return array.reduce((a, b) => +a + +b, 0);
    },
    hasOneAnswer() {
      if (this.task?.task_units?.length) {
        if (this.task?.task_units[0].possible_answers?.length) {
          return true;
        }
      }
      return false;
    },
    update() {
      if (this.$route.query.type === 'draft') {
        return false;
      }
      return this.$route.meta.update;
    },
  },
  created() {
    this.$store.dispatch('GET_RESOURCES');
    if (this.$route.meta.update) this.getJournalByTask();
  },
  mounted() {
    if (this.$route.meta.update) this.loadTask(this.$route.params.id);
    this.getTagList();
  },
  methods: {
    startReCalculate() {
      this.$rHandler(
        TaskService.startReCalculate(this.$route.params.id),
      );
    },
    getJournalByTask() {
      const params = {
        'search[task_id]': this.$route.params.id,
      };
      this.$rHandler(
        this.$api.journal.getJoarnalAnswer({ params }),
        ({ data }) => {
          this.canCreateQuestion = data.data.length || !data.data.length;
          // TODO: раскомменть потом
          // this.canCreateQuestion = !data.data.length;
        },
      );
    },
    updateDraft() {
      const params = {
        ...this.task,
        relation: [{
          model: this.$route.query.model,
          id: this.$route.query.id,
        }],
      };
      if (!this.taskDraftId) {
        this.$rHandler(
          TaskService.createDraft(params),
          (response) => {
            this.taskDraftId = response.data.data.id;
          },
        );
      } else {
        this.$rHandler(
          TaskService.updateDraft(this.taskDraftId, params),
        );
      }
    },
    addTaskUnit() {
      if (!this.task.task_units) {
        this.$set(this.task, 'task_units', []);
      }
      this.task.task_units.push({
        title: '',
        description: '',
        type: '',
        ordinal: this.task.task_units.length + 1,
      });
      this.$nextTick(() => {
        this.$vuetify.goTo(this.$refs[`taskUnit${this.task.task_units.length - 1}`][0]);
      });
    },
    removeTaskUnit(index) {
      this.task.task_units.splice(index, 1);
    },
    createTask() {
      let payload = {};
      if (!this.task.task_units) {
        this.$toasted.error('Добавьте хотя бы один вопрос', { duration: 3000 });
        return;
      }
      this.loadingButton = true;
      payload = {
        ...this.task,
        task_draft_id: this.taskDraftId,
        relation_model: this.$route.query.model,
        relation_id: this.$route.query.id,
        mark: this.task.type === this.$conf.TASK_TYPES.mixed ? this.task.mark : 1,
      };
      this.$rHandler(
        TaskService.createTask(payload),
        () => {
          this.$toasted.success('Задание добавлено', { duration: 3000, icon: 'mdi-check' });
          this.$emit('save');
          this.loadingButton = false;
          setTimeout(() => {
            this.$router.go(-1);
          }, 1500);
        },
        (response) => {
          this.loadingButton = false;
          this.$toasted
            .error(response.response.data.validation.possible_answers, { duration: 3000 });
        },
        this.$refs.obs,
      );
    },
    updateTask() {
      const taskUnits = [...this.task.task_units];
      const taskForSend = {
        ...this.task,
        relation_model: this.$route.query.model,
        relation_id: this.$route.query.id,
      };
      taskForSend.task_units = [...taskUnits];
      this.loadingButton = true;
      TaskService.updateTask(taskForSend)
        .then(() => {
          this.$toasted.success('Задание обновлено', { duration: 3000, icon: 'mdi-check' });
          this.$emit('save');
          this.startReCalculate();
          this.$router.go(-1);
        })
        .catch((err) => {
          const { response } = err;
          if (response.data.result_code === 'VAL01') {
            this.$toasted
              .error(response.data.validation.possible_answers, { duration: 3000 });
            try {
              this.$refs.obs.setErrors(response.data.validation);
            } catch (e) {
              // console.log(e);
            }
          }
        })
        .finally(() => { this.loadingButton = false; });
    },
    addUpdateMethod(unit) {
      if (!this.task.task_units) this.$set(this.task, 'task_units', []);
      if (unit.id && unit.method !== 'delete') this.$set(unit, 'method', 'update');
    },
    addDeleteMethod(unit) {
      if (unit.id) this.$set(unit, 'method', 'delete');
    },
    loadTask(id) {
      this.loadingTask = true;
      let request;
      let data;
      const params = { expand: 'task_units,relation_id' };
      switch (this.$route.query.type) {
        case 'draft':
          request = 'getDrafts';
          data = this.$route.query.draftId;
          break;
        default:
          request = 'getTask';
          data = id;
          break;
      }
      TaskService[request](data, params)
        .then((r) => {
          this.templateName = r.data.data.name;
          this.task = r.data.data.draft_task || r.data.data;
          if (this.$route.query.type === 'draft') this.taskDraftId = r.data.data.id;
          this.loadingTask = false;
        })
        .catch(() => { this.$toasted.error('Ошибка при загрузке задания', { duration: 2000 }); });
    },
    getTagList() {
      this.$rHandler(
        this.$api.tag.index(),
        (r) => {
          this.tags = r.data.data;
        },
      );
    },
    invertColor,
  },
};
</script>

<style lang="scss" scoped>

</style>
